// frontend/src/api/almuerzosService.js
import api from './api';

const almuerzosService = {

  // Dashboard
  async getDashboard() {
    const res = await api.get('/almuerzos/dashboard');
    return res.data;
  },

  // =============================================
  // MENÚS
  // =============================================
  async getMenus(params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await api.get(`/almuerzos/menus${query ? '?' + query : ''}`);
    return res.data;
  },

  async getMenuActivo(tipo = 'Almuerzo') {
    const res = await api.get(`/almuerzos/menus?tipo=${tipo}&estado=Activo`);
    return res.data?.[0] || null;
  },

  async getCombosDeMenu(menuID) {
    const res = await api.get(`/almuerzos/menus/${menuID}/combos`);
    return res.data;
  },

  async crearMenu(datos) {
    const res = await api.post('/almuerzos/menus', datos);
    return res.data;
  },

  // NUEVO: actualiza los combos de un menú ya publicado
  async actualizarMenu(menuID, datos) {
    const res = await api.put(`/almuerzos/menus/${menuID}`, datos);
    return res.data;
  },

  // Solo para menús cerrados; fechaInicioConfirmacion (YYYY-MM-DD) debe ser la fecha de inicio del menú
  async eliminarMenu(menuID, fechaInicioConfirmacion) {
    const res = await api.delete(`/almuerzos/menus/${menuID}`, { data: { fechaInicioConfirmacion } });
    return res.data;
  },

  async cambiarEstadoMenu(menuID, estado) {
    const res = await api.put(`/almuerzos/menus/${menuID}/estado`, { estado });
    return res.data;
  },

  // NUEVO: marca el menú como enviado al proveedor/oferente
  async enviarAlOferente(menuID) {
    const res = await api.post(`/almuerzos/menus/${menuID}/enviar`);
    return res.data;
  },

  // NUEVO: publica el menú y envía notificación a todos los colaboradores
  async publicarMenuConNotificacion(menuID, enviarNotificacion = true) {
    const res = await api.post(`/almuerzos/menus/${menuID}/publicar`, { enviarNotificacion });
    return res.data;
  },

  // =============================================
  // SOLICITUDES
  // =============================================
  async getSolicitudes(params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await api.get(`/almuerzos/solicitudes${query ? '?' + query : ''}`);
    return res.data;
  },

  async getMisSolicitudes(empleadoID, params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await api.get(`/almuerzos/mis-solicitudes/${empleadoID}${query ? '?' + query : ''}`);
    return res.data;
  },

  async crearSolicitud(datos) {
    const res = await api.post('/almuerzos/solicitudes', datos);
    return res.data;
  },

  async cancelarSolicitud(solicitudID, motivo = '') {
    const res = await api.put(`/almuerzos/solicitudes/${solicitudID}/cancelar`, { motivo });
    return res.data;
  },

  // =============================================
  // REPORTES
  // =============================================
  async getReporteSemanal(semana, año, tipo = '') {
    const params = new URLSearchParams({ semana, año, ...(tipo && { tipo }) }).toString();
    const res = await api.get(`/almuerzos/reportes/semanal?${params}`);
    return res.data;
  },

  async getReporteGeneral(params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await api.get(`/almuerzos/reportes/general${query ? '?' + query : ''}`);
    return res.data;
  },

  async getReporteOferente(semana, año) {
    const res = await api.get(`/almuerzos/reportes/oferente?semana=${semana}&año=${año}`);
    return res.data;
  },

  // =============================================
  // OFERENTE (proveedor de almuerzo)
  // =============================================
  async getOferente() {
    const res = await api.get('/almuerzos/oferente');
    return res.data;
  },

  async guardarOferente(datos) {
    const res = await api.put('/almuerzos/oferente', datos);
    return res.data;
  },

  async getHistorialOferente() {
    const res = await api.get('/almuerzos/oferente/historial');
    return res.data;
  },

  // =============================================
  // CONFIGURACIÓN (recordatorios y apertura/cierre automático)
  // =============================================
  async getConfiguracion() {
    const res = await api.get('/almuerzos/configuracion');
    return res.data;
  },

  async guardarConfiguracion(datos) {
    const res = await api.put('/almuerzos/configuracion', datos);
    return res.data;
  },

  // =============================================
  // CARGA DE MENÚ POR EXCEL
  // =============================================
  // Descarga un Excel generado por el backend usando el nombre de archivo que este indica
  async _descargarExcel(url, nombrePorDefecto) {
    let res;
    try {
      res = await api.get(url, { responseType: 'blob' });
    } catch (e) {
      // Con responseType 'blob' el error del backend llega como Blob: se convierte a JSON para poder mostrarlo
      const data = e.response?.data;
      if (data instanceof Blob) {
        try { e.response.data = JSON.parse(await data.text()); } catch { /* se deja tal cual */ }
      }
      throw e;
    }
    const disposition = res.headers?.['content-disposition'] || '';
    const nombre = disposition.match(/filename="?([^";]+)"?/)?.[1] || nombrePorDefecto;
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', nombre);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },

  // Plantilla del menú (formato CEIRD) con el rango de fechas de la semana indicada
  async descargarPlantillaMenu({ fechaInicio, tipoMenu } = {}) {
    const query = new URLSearchParams({ ...(fechaInicio && { fechaInicio }), ...(tipoMenu && { tipoMenu }) }).toString();
    await this._descargarExcel(`/almuerzos/menus/plantilla${query ? '?' + query : ''}`, 'plantilla-menu-almuerzo.xlsx');
  },

  async cargarMenuDesdeExcel(datos, archivo) {
    const formData = new FormData();
    // "año" se envía como "anio": multer no decodifica bien la ñ en los nombres de campo multipart
    Object.entries(datos).forEach(([k, v]) => formData.append(k === 'año' ? 'anio' : k, v));
    formData.append('archivo', archivo);
    const res = await api.post('/almuerzos/menus/cargar-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  // =============================================
  // CARGA MASIVA DE SOLICITUDES POR EXCEL
  // =============================================
  async descargarPlantillaSolicitudes(menuID) {
    await this._descargarExcel(`/almuerzos/solicitudes/plantilla?menuID=${menuID}`, 'seleccion-almuerzo.xlsx');
  },

  // soloValidar = true devuelve la vista previa (resumen y errores) sin registrar nada
  async cargarSolicitudesDesdeExcel(menuID, archivo, soloValidar, enviarCorreos = false) {
    const formData = new FormData();
    formData.append('menuID', menuID);
    formData.append('soloValidar', soloValidar ? 'true' : 'false');
    formData.append('enviarCorreos', enviarCorreos ? 'true' : 'false');
    formData.append('archivo', archivo);
    const res = await api.post('/almuerzos/solicitudes/cargar-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  // =============================================
  // EMPLEADOS
  // =============================================
  async getEmpleados() {
    const res = await api.get('/almuerzos/empleados');
    return res.data;
  },

  // =============================================
  // UTILIDADES DE FECHA
  // Estas funciones deben mantenerse idénticas a las del componente
  // para que el service y el UI siempre calculen el mismo lunes.
  // Semana 1 = semana que contiene el primer lunes del año.
  // =============================================
  getSemanaActual() {
    const d = new Date();
    const año = d.getFullYear();
    const primerDia = new Date(año, 0, 1);
    const dow = primerDia.getDay(); // 0=Dom, 1=Lun...
    const diasHastaPrimerLunes = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
    const primerLunes = new Date(año, 0, 1 + diasHastaPrimerLunes);
    const diffDias = Math.floor((d - primerLunes) / 86400000);
    if (diffDias < 0) return 1;
    return Math.floor(diffDias / 7) + 1;
  },

  getLunesDeSemana(semana, año) {
    const primerDia = new Date(año, 0, 1);
    const dow = primerDia.getDay();
    const diasHastaPrimerLunes = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
    const primerLunes = new Date(año, 0, 1 + diasHastaPrimerLunes);
    const lunes = new Date(primerLunes);
    lunes.setDate(primerLunes.getDate() + (semana - 1) * 7);
    return lunes;
  },

  getDiasSemana(lunes) {
    const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    return dias.map((nombre, i) => {
      const fecha = new Date(lunes);
      fecha.setDate(fecha.getDate() + i);
      const y = fecha.getFullYear();
      const m = String(fecha.getMonth() + 1).padStart(2, '0');
      const d = String(fecha.getDate()).padStart(2, '0');
      return { nombre, fecha: `${y}-${m}-${d}` };
    });
  }
};

export default almuerzosService;